// eslint-disable-next-line no-unused-vars
const analyticTypes = require('../../jsdoc-types');
const analyticUtils = require('../../analyticUtils');
const { getPidWithoutSize, getVariationGroupId } = require('../../../common/helpers/productIdHelpers');
const {
  getDoubleLookCodeFromPid, getOriginalPID, isDoubleLook,
} = require('../../../common/utils/doubleLookUtils');
const { searchDataStorage } = require('../../stores/searchData');
const { getItemListForProductID } = require('../../stores/itemList');
const { LIST_NAMES } = require('../../analyticsConst');

/**
 * Ecommerce Event Items GA4 object array for GA4 Events
 * @typedef {object[]} EcommerceEventItemsGA4
 * @property {string} add_to_cart_place - The type of page from which the product was added to the shopping basket.
 * @property {number} discount - Product discount percentage number.
 * @property {string} grid_view_type - The type of grid view from which the user accessed the product.
 * @property {string} index - The index/position of the item in a list.
 * @property {string} item_category - Product category.
 * @property {string} item_category2 - Product subcategory
 * @property {string} item_id - Product SKU.
 * @property {string} item_list_id - Product List ID. Category-{{categoryName}} or Subcategory-{{subcategoryName}}
 * @property {string} item_name - Product name.
 * @property {string} item_variant - Color.
 * @property {string} price - Product price with discount.
 * @property {string} product_double_look - 'yes' or 'no' referencing if the product is a double look.
 * @property {string} product_id_double_look - The DLX double look product, 'no' if not a DL product.
 * @property {string} product_id_with_size - Product SKU including the size.
 * @property {string} product_label - The product label.
 * @property {string} product_normal_price - Product price without discounts.
 * @property {string} product_parent_id - Product SKU without size.
 * @property {string} product_size - Product size.
 * @property {string} product_stock_availability - 'yes or 'no' referencing if the product has stock.
 * @property {number} quantity - Product quantity.
 * @property {string} searched_term - The term the user has used in the search to access the product.
 * @property {string} search_engine - The engine the user has used in the search.
 */

/**
 * Extra entries for a single item of an ecommerce event
 * @typedef {object} AbstractProductLineItemExtensionForEcommerceEventItemsGA4
 * @property {object} itemProps Entries that the final item of the event will
 * have. These will overwrite.
 */

/**
 * Mixed object with tha data of the product line item plus some direct event
 * item entries.
 * @typedef {ProductLineItem && AbstractProductLineItemExtensionForEcommerceEventItemsGA4} ProductLineItemExtensionForEcommerceEventItemsGA4
 */

/**
 * Create an EcommerceEventItemsGA4
 * @param {ProductLineItemExtensionForEcommerceEventItemsGA4[]} productLineItems
 * Array with the info of every product line item and extra event item entries
 * used to build the EcommerceEventItemsGA4 object
 * @param {Object} [options] options
 * @param {Storage} [options.cartItemListStorage] Storage to get the itemList
 * of a product
 * @param {Storage} [options.targetSearchDataStorage] Storage to get the search
 * data of a product
 * @returns {EcommerceEventItemsGA4} The array of created items
 */
function createEcommerceEventItemsGA4(productLineItems, {
  cartItemListStorage,
  targetSearchDataStorage = searchDataStorage,
} = {}) {
  const dimensions = analyticUtils.getDimensionsFromLocalStorage();

  return productLineItems.map((product) => {
    const {
      itemProps,
    } = product;

    const currentDimension = dimensions.find((hit) => hit.pid === product.id);

    const productID = product.doubleLookPID || product.id;

    let itemList;
    let position;
    if (cartItemListStorage) {
      const cartVariantId = product.id;
      const itemListAndPosition = cartItemListStorage.getItem(cartVariantId);
      itemList = itemListAndPosition?.list;
      position = itemListAndPosition?.position;
    }
    if (!itemList) {
      const impressedProductId = getVariationGroupId(productID);
      itemList = getItemListForProductID(impressedProductId);
    }

    const { color } = analyticUtils.getDisplayValuesOfVariationAttributes(
      product.variationAttributes,
    );

    const isProductDoubleLook = isDoubleLook(productID);

    /** @type {analyticTypes.SearchDataOfProduct} */
    const searchData = targetSearchDataStorage.getItem(productID) // get searchData for variant (ej: cart)
      || targetSearchDataStorage.getItem(getPidWithoutSize(productID)); // if not, for variation group

    // Updating item_list_id when comes from search or search modal
    if (itemList === LIST_NAMES.other && searchData?.match) {
      itemProps.item_list_id = LIST_NAMES.search;
      itemProps.searched_term = searchData.query;
    }
    const tagName = analyticUtils.getProductTagName(product);

    return {
      discount: product.price?.discount || 0,
      grid_view_type: currentDimension?.d45 || analyticUtils.getColumnModeFromSessionStorage(),
      index: position,
      item_category: product.parentCategoryName,
      item_category2: product.bylSubcategoryName || undefined,
      item_id: getPidWithoutSize(productID),
      item_list_id: itemList,
      item_name: product.productName,
      item_variant: color,
      price: analyticUtils.getNumericValueWithDecimals(product?.price?.sales?.decimalPrice),
      product_double_look: isProductDoubleLook ? 'yes' : 'no',
      product_id_double_look: isProductDoubleLook ? getDoubleLookCodeFromPid(productID) : 'no',
      product_id_with_size: getOriginalPID(productID),
      product_label: tagName,
      product_normal_price: product.price?.list?.decimalPrice
        ? analyticUtils.getNumericValueWithDecimals(product.price?.list?.decimalPrice)
        : analyticUtils.getNumericValueWithDecimals(product?.price?.sales?.decimalPrice),
      product_parent_id: getPidWithoutSize(productID),
      product_stock_availability: product.available ? 'yes' : 'no',
      quantity: product.quantity || 1,
      searched_term: searchData?.query || undefined,
      search_engine: gaInfo.searchEngineType,
      ...itemProps,
    };
  });
}

module.exports = {
  createEcommerceEventItemsGA4,
};
